import {
  DollarOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Table,
  type TablePaginationConfig,
  type TableProps,
  message
} from 'antd'
import type { ColumnsType, SorterResult } from 'antd/es/table/interface'
import moment from 'moment/moment'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import InstitutionUserService from '../../services/InstitutionUserService'
import { downloadExcel } from '../../utils/downloadExcel'

const antIcon = <LoadingOutlined style={{ fontSize: 'var(--base-font-size)', color: '#37762F' }} spin />
const { REACT_APP_API_BASE_URL } = process.env
interface PaymentsMeta {
  total: number
  pageSize: number
  currentPage: number
}
interface DataType {
  key: React.Key
  created_at: string
  type: string
  ref_number: string
  full_name: string
  phone_number: string
  expiration_date: string
  status: string
}

const BidGuaranteesList: React.FC = () => {
  const [form] = Form.useForm()
  const userInfo = JSON.parse(localStorage.getItem('userInfo') ?? '{}')
  const navigate = useNavigate()
  const [bidGuaranteeList, setBidGuaranteeList] = useState([])
  const [paymentsMeta, setPaymentsMeta] = useState<PaymentsMeta | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [clear, setClear] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sortedInfo, setSortedInfo] = useState<SorterResult<DataType>>({})
  const [paginating, setPaginating] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0
  })
  const [propertyIDs, setPropertyIDs] = useState<string[]>([])
  const [statusBid, setStatusBid] = useState()
  const [selectedPID, setSeletedPID] = useState()

  useEffect(() => {
    OnBidSecuritySearch()
    // eslint-disable-next-line
  }, [clear, paginating.current, paginating.pageSize, statusBid, selectedPID])

  const OnBidSecuritySearch = (event?: any): void => {
    setLoading(true)

    if (userInfo.role === 'SYSTEM ADMIN' || userInfo.role === 'MINIJUST FINANCE' || userInfo.role === 'SYSTEM SUPPORT') {
      InstitutionUserService.viewBidGuarantee(
        paginating.pageSize as number,
        paginating.current as number,
        event?.target?.value,
        statusBid,
        selectedPID
      )
        .then((res: any) => {
          if (res.status === 200) {
            setBidGuaranteeList(res?.data)
            const pIDSet = new Set<string>(
              res?.data.map((item: Record<string, any>) => item.property_id)
            )
            const uniquePIDs: string[] = Array.from(pIDSet)
            setPropertyIDs(uniquePIDs)
            setPaginating((prev) => ({
              ...prev,
              total: res?.meta?.total
            }))
            setLoading(false)
          }
        })
        .catch((err: any) => {
          if (err?.message !== 'Operation canceled due to new request.') {
            void message.error(err?.message)
          }
          setLoading(false)
        })
    } else if (userInfo.role === 'INSTITUTION ADMIN' || userInfo.role === 'INSTITUTION USER' || userInfo.role === 'SYSTEM SUPPORT') {
      InstitutionUserService.GetBidSecurityListInstitutions(
        paginating.pageSize as number,
        paginating.current as number,
        event?.target?.value,
        statusBid,
        selectedPID
      )
        .then((res: any) => {
          if (res.status === 200) {
            setBidGuaranteeList(res?.data)
            const pIDSet = new Set<string>(
              res?.data.map((item: Record<string, any>) => item.property_id)
            )
            const uniquePIDs: string[] = Array.from(pIDSet)
            setPropertyIDs(uniquePIDs)
            setPaginating((prev) => ({
              ...prev,
              total: res?.meta?.total
            }))
            setLoading(false)
          }
        })
        .catch((err: any) => {
          if (err?.message !== 'Operation canceled due to new request.') {
            void message.error(err?.message)
          }
          setLoading(false)
        })
    }
  }

  const clearFilter = (): void => {
    form.resetFields()
    setSeletedPID(undefined)
    setStatusBid(undefined)
    setClear(true)
  }

  const columns: ColumnsType<DataType> = [
    {
      title: 'Bidder',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text: any, record: any) => <span>{record?.full_name}</span>,
      sorter: (a: any, b: any) => a?.full_name.localeCompare(b?.ref_number),
      sortOrder: sortedInfo.columnKey === 'full_name' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Phone No',
      dataIndex: 'phone_number',
      key: 'phone_number',
      render: (text: any, record: any) => <span>{record?.phone_number}</span>,
      ellipsis: true
    },
    {
      title: 'Property ID',
      dataIndex: 'property_id',
      render: (text: any, record: any) => <span>{record?.property_id}</span>,
      key: 'property_id'
    },
    {
      title: 'Payer Code',
      dataIndex: 'payer_code',
      key: 'payer_code',
      render: (text: any, record: any) => <span>{record?.payer_code}</span>,
      sorter: (a: any, b: any) => a?.payer_code.localeCompare(b?.payer_code),
      sortOrder: sortedInfo.columnKey === 'ref_number' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Payment Channel',
      dataIndex: 'payment_channel',
      key: 'payment_channel',
      render: (text: any, record: any) => <span>{record?.payment_channel}</span>,
      sorter: (a: any, b: any) => a?.payment_channel.localeCompare(b?.payment_channel),
      sortOrder: sortedInfo.columnKey === 'payment_channel' ? sortedInfo.order : null,
      ellipsis: true
    },
    {
      title: 'Channel Name',
      dataIndex: 'payment_channel_name',
      key: 'payment_channel_name',
      render: (text: any, record: any) => <span>{record?.payment_channel_name}</span>,
      sorter: (a: any, b: any) => a?.payment_channel_name.localeCompare(b?.payment_channel_name),
      sortOrder: sortedInfo.columnKey === 'payment_channel_name' ? sortedInfo.order : null,
      ellipsis: true
    },
    {
      title: 'Ref Number',
      dataIndex: 'ref_number',
      key: 'ref_number',
      render: (text: any, record: any) => <span>{record?.ref_number}</span>,
      sorter: (a: any, b: any) => a?.ref_number.localeCompare(b?.ref_number),
      sortOrder: sortedInfo.columnKey === 'ref_number' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Payment Date',
      dataIndex: 'payment_date_time',
      key: 'payment_date_time',
      render: (text: any, record: any) => (
        <span>{moment(record?.payment_date_time).format('YYYY-MM-DD HH:MM:SS')}</span>
      ),
      sorter: (a: any, b: any) => moment(a?.payment_date_time).unix() - moment(b?.payment_date_time).unix(),
      sortOrder: sortedInfo.columnKey === 'payment_date_time' ? sortedInfo.order : null,
      ellipsis: false
    },
    {
      title: 'Details',
      dataIndex: 'details',
      render: (text: any, record: any) => (
        <div>
          <i
            className="far fa-eye cursor-pointer"
            onClick={() => {
              navigate('/view-bid-guarantees', { state: record })
            }}
          ></i>
          &nbsp;&nbsp;
          {userInfo?.role === 'INSTITUTION USER' &&
            record?.type !== 'PAYMENT' &&
            record?.used === false && (
              <>
                <i
                  className="fas fa-edit cursor-pointer"
                  onClick={() => {
                    editBidSecurity(record)
                  }}
                ></i>
                &nbsp;&nbsp;
              </>)}
          &nbsp;&nbsp;<i className="las la-file-prescription cursor-pointer text-base"></i>
        </div>
      )
    }
  ]

  const editBidSecurity = (record: any): void => {
    navigate('/create-bid-guarantee', { state: record })
  }

  const handleChange: TableProps<DataType>['onChange'] = (pagination, _filters, sorter) => {
    setPaginating(pagination)
    setSortedInfo(sorter as SorterResult<DataType>)
  }

  const createInstitution = (): void => {
    navigate('/create-bid-guarantee')
  }

  const onPIDSelect = (event: any): void => {
    setSeletedPID(event)
  }

  const onStatusSelect = (event: any): void => {
    setStatusBid(event)
  }

  const columnsToDownload = [
    'full_name',
    'nid',
    'phone_number',
    'email',
    'created_at',
    'execution_case_no',
    'property_id',
    'name',
    'security_amount',
    'expiration_date',
    'ref_number',
    'type',
    'doc_number',
    'bs_expiration_date'
  ]

  const handleClearSelect = (field: string): void => {
    if (field === 'property') {
      setSeletedPID(undefined)
    } else if (field === 'status') {
      setStatusBid(undefined)
    }
  }
  // Format date
  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }
    return date.toLocaleDateString(undefined, options)
  }

  // Format time
  const formatTime = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }
    return date.toLocaleTimeString(undefined, options)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const generateCalendar = () => {
    const year = currentTime.getFullYear()
    const month = currentTime.getMonth()
    const today = currentTime.getDate()

    // Day names
    const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    // Month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]

    // Create calendar grid
    const calendarDays = []

    // Add day headers
    dayNames.forEach((day) => {
      calendarDays.push(
        <div key={`header-${day}`} className="text-center text-standardall font-medium py-1">
          {day}
        </div>
      )
    })

    // Get the current day of the week (0-6)
    const currentDayOfWeek = new Date(year, month, today).getDay()

    // Calculate the start date (go back to find Sunday of the current week)
    const startDay = today - currentDayOfWeek // Adjust to show the correct week (centered on today)

    // We'll show only one week (7 days centered around the current day)
    for (let i = 0; i < 7; i++) {
      const displayDay = startDay + i
      const displayDate = new Date(year, month, displayDay)
      const dayOfMonth = displayDate.getDate()
      const isCurrentMonth = displayDate.getMonth() === month
      const isToday = isCurrentMonth && dayOfMonth === today

      calendarDays.push(
        <div
          key={`day-${i}`}
          className={`text-center py-1 text-standard ${
            isToday
              ? 'bg-blue-500 text-white rounded-full font-bold'
              : isCurrentMonth
                ? ''
                : 'text-gray-300'
          }`}
        >
          {dayOfMonth}
        </div>
      )
    }

    return (
      <div className="mt-2">
        <div className="text-center font-medium mb-2">
          {monthNames[month]} {year}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {calendarDays}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Enhanced Custom CSS Styles */}
      <style>{
        `
        .enhanced-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .enhanced-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        .enhanced-input {
          border-radius: 8px;
          border: 2px solid #e5e7eb;
          transition: border-color 0.2s ease;
        }
        .enhanced-input:focus {
          border-color: #37762f;
          box-shadow: 0 0 0 3px rgba(55, 118, 47, 0.1);
        }
        .enhanced-button {
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .enhanced-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .enhanced-table-row:nth-child(even) {
          background-color: #f8fafc;
        }
        .enhanced-table-row:hover {
          background-color: #e2e8f0;
        }
        .enhanced-table-header {
          background: linear-gradient(135deg, #37762f 0%, #4a7c59 100%);
          color: white;
          font-weight: 600;
        }
        `
      }</style>

      {/* Enhanced Page Header */}
      <div className="px-5 py-4 border-b-slate-200 border border-solid w-full" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px'
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-bold flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '16px',
              boxShadow: '0 4px 12px rgba(55, 118, 47, 0.3)'
            }}>
              <span style={{ fontSize: '24px' }}>🛡️</span>
            </div>
            <div>
              <h2 className="text-title font-bold" style={{
                margin: 0,
                background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Bid Guarantees
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>
                🛡️ Guarantee Processing • Financial Transactions
              </p>
            </div>
          </div>
          {userInfo?.role === 'INSTITUTION USER' && (
            <div className="md:w-1/5">
              <Button
                type="primary"
                size="large"
                className="w-full enhanced-button"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => {
                  createInstitution()
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)'
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)'
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
              >
                <div className="float-left">
                  <i className="fa-solid fa-plus-minus"></i>&nbsp;
                </div>
                <div className="text-base">➕ Add Bid Security</div>
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className="px-7">
        <div className="py-1">
          <Form
            name="normal_login"
            layout="vertical"
            className="login-form p-2"
            initialValues={{ remember: true }}
            onFinish={clearFilter}
            form={form}
          >
            <Card className="enhanced-card" style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
            }}>
              <Row gutter={16}>
                <Col xs={24} xl={9}>
                  <Form.Item name="property">
                    <Select
                      showSearch
                      onChange={onPIDSelect}
                      size="large"
                      value={selectedPID}
                      placeholder="Filter by Property ID"
                      allowClear
                      onClear={() => { handleClearSelect('property') }}
                      className="enhanced-input"
                      style={{
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        transition: 'border-color 0.2s ease'
                      }}
                    >
                      {propertyIDs.map((pid) => (
                        <Select.Option key={pid} value={pid}>
                          {pid}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} xl={9}>
                  <Form.Item name="status">
                    <Select
                      onChange={onStatusSelect}
                      size="large"
                      value={statusBid}
                      placeholder="Filter by Bid Status "
                      allowClear
                      onClear={() => { handleClearSelect('status') }}
                      className="enhanced-input"
                      style={{
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        transition: 'border-color 0.2s ease'
                      }}
                    >
                      <Select.Option value="WON">Won</Select.Option>
                      <Select.Option value="LOST">Lost</Select.Option>
                      <Select.Option value="ONGOING">Ongoing</Select.Option>
                    </Select>
                  </Form.Item>
                  {selectedPID !== undefined && statusBid !== undefined
                    ? (
                      <div className="flex justify-between pb-2.5 md:pb-0">
                        <div style={{
                          background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #3b82f6',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>
                          Applied filters:{' '}
                          <span className="font-medium" style={{ color: '#1d4ed8' }}>
                            {selectedPID}, {statusBid}
                          </span>
                        </div>
                        <div
                          className="enhanced-button"
                          style={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            border: 'none',
                            boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)',
                            transition: 'all 0.2s ease'
                          }}
                          onClick={clearFilter}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-1px)'
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.4)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)'
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.3)'
                          }}
                        >
                          🗑️ Clear Filters
                        </div>
                      </div>)
                    : ('')}
                </Col>
                <Col xs={24} xl={6}>
                  <Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      className="w-full enhanced-button"
                      style={{
                        background: selectedPID !== undefined && statusBid !== undefined
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        height: '48px',
                        fontSize: '16px',
                        fontWeight: '600',
                        boxShadow: selectedPID !== undefined && statusBid !== undefined
                          ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                          : '0 2px 4px rgba(156, 163, 175, 0.3)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={() => {
                        downloadExcel(columnsToDownload, bidGuaranteeList, 'bid guarantees')
                      }}
                      disabled={selectedPID === undefined || statusBid === undefined}
                      onMouseEnter={(e) => {
                        if (selectedPID !== undefined && statusBid !== undefined) {
                          e.currentTarget.style.transform = 'translateY(-1px)'
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPID !== undefined && statusBid !== undefined) {
                          e.currentTarget.style.transform = 'translateY(0)'
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)'
                        }
                      }}
                    >
                      <div className="float-left">
                        <i className="fa-file-excel"></i>
                      </div>
                      📊 DOWNLOAD
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
        </div>
        {/* Responsive style for summary card */}
        <style>
          {`
            @media (max-width: 600px) {
              .responsive-summary-card {
                max-width: 100% !important;
                width: 100% !important;
                min-width: 0 !important;
              }
            }
          `}
        </style>
        {/* Enhanced Summary Section */}
        <div
          className="responsive-summary-card enhanced-card"
          style={{
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)',
            padding: '16px 20px',
            gap: 0,
            minHeight: 80,
            border: '1px solid #22c55e',
            maxWidth: 420,
            width: '100%',
            margin: '4px 0 4px auto',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)'
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(34, 197, 94, 0.2)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(34, 197, 94, 0.15)'
          }}
        >
          <div className="flex-1 flex flex-col items-center justify-center">
            <div style={{
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '8px',
              boxShadow: '0 4px 8px rgba(34, 197, 94, 0.3)'
            }}>
              <span style={{ fontSize: '20px' }}>🛡️</span>
            </div>
            <span style={{ fontSize: '14px', color: '#374151', fontWeight: 600, marginBottom: '4px' }}>Total Bid Guarantees</span>
            <span style={{ fontSize: '24px', fontWeight: 700, color: '#16a34a', lineHeight: 1 }}>{((paymentsMeta?.total) != null) ? paymentsMeta.total : 0}</span>
          </div>
        </div>
        <Card
          className="enhanced-card"
          title={
            <span style={{
              background: 'var(--refund-theme-primary-soft)',
              color: '#111827',
              padding: '12px 20px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📋 List of Provided Bid Guarantees ({paymentsMeta?.total ?? 0})
            </span>
          }
          style={{
            marginTop: '12px',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb'
          }}
          headStyle={{
            background: 'transparent',
            borderRadius: '12px 12px 0 0'
          }}
        >
          <div className="pt-2.5">
            {/* Enhanced Search Bar */}
            <div className="search-container" style={{ width: '100%', marginBottom: 16 }}>
              <Input
                size="large"
                placeholder="🔍 Quick search in the tables"
                prefix={<ZoomOutOutlined className="search-icon" style={{ color: '#37762f' }} />}
                onChange={(e) => {
                  OnBidSecuritySearch(e)
                }}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  transition: 'border-color 0.2s ease'
                }}
                className="enhanced-input"
                onFocus={(e) => {
                  e.target.style.borderColor = '#37762f'
                  e.target.style.boxShadow = '0 0 0 3px rgba(55, 118, 47, 0.1)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb'
                  e.target.style.boxShadow = 'none'
                }}
              />
            </div>
            <div className="pt-2.5 overflow-auto">
              {loading
                ? (<div className="text-center justify-center align-middle py-4">
                  <Spin indicator={antIcon} />
                </div>)
                : (
                  <div className="w-full overflow-x-auto">
                    <div style={{ minWidth: '900px' }}>
                      <Table
                        columns={columns}
                        dataSource={bidGuaranteeList}
                        pagination={{
                          ...paginating,
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          hideOnSinglePage: false,
                          pageSizeOptions: ['10', '20', '50', '100'],
                          showLessItems: true,
                          style: {
                            background: '#F5F5F5',
                            borderRadius: '8px',
                            padding: '12px',
                            border: '1px solid #e5e7eb',
                            marginTop: '16px'
                          }
                        }}
                        onChange={handleChange}
                        size="small"
                        className="enhanced-table"
                        style={{
                          borderRadius: '8px',
                          overflow: 'hidden'
                        }}
                        rowClassName={(record, index) =>
                          index % 2 === 0 ? 'enhanced-table-row' : 'enhanced-table-row-alt'
                        }
                        components={{
                          header: {
                            cell: (props: any) => (
                              <th
                                {...props}
                                className="enhanced-table-header"
                                style={{
                                  color: 'white',
                                  fontWeight: '600',
                                  border: 'none',
                                  padding: '12px 8px'
                                }}
                              />
                            )
                          }
                        }}
                      />
                    </div>
                  </div>
                  )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default BidGuaranteesList
